// ignore-string-externalization

import React from 'react';
import { makeContentfulAsset } from '../Contentful';
import { isContentfulImage } from '../utils';
import {
  ContentfulImage,
  lightCaptionStyle,
} from '../Contentful/ContentfulImage';
import { DefaultRenderer } from './DefaultRenderer';
import { ImageAspectBox16x9 } from '@spotify-internal/frodor-modules';
import { renderToString } from 'react-dom/server';
import { maxWidth } from '../Module/ArticleModule/styleConstants';
import qs from 'query-string';

import styled from 'styled-components';

import {
  display1FontSize,
  display1FontWeight,
  spacer4,
  spacer8,
  spacer80,
} from '@spotify-internal/encore-web-v5';

const dropCapStyle = `
  padding-left: ${spacer4};
  padding-bottom: 0px;
  padding-right: ${spacer8};
  float: left;
  position: relative;
  top: ${spacer4};
  margin-bottom: ${spacer4};
  margin-left: ${spacer8};
  margin-right: ${spacer4};
  font-size: ${display1FontSize};
  line-height: ${spacer80};
  font-weight: ${display1FontWeight};
`;

const DropCap = styled.p`
  max-width: ${maxWidth};
  margin: auto;
  &:first-letter {
    ${dropCapStyle}
  }
`;

export class ArticleRenderer extends DefaultRenderer {
  pNum = 0;
  initialDropcap = false;

  image(href: string, title: string, text: string) {
    const fullWidth = (text || '').indexOf('full-width') > -1;

    // contentful image: display as natural-width responsive asset
    const isContentfulImg = isContentfulImage(href);

    if (isContentfulImg && !fullWidth) {
      const bits = href.split('.');
      const contentType = `image/${bits[bits.length - 1].replace(
        'jpg',
        'jpeg',
      )}`;
      const asset = makeContentfulAsset({
        url: href,
        contentType,
        description: title || text,
      });

      // TODO: unhack caption styling
      return renderToString(
        <ContentfulImage
          asset={asset}
          caption={text}
          captionStyle={lightCaptionStyle}
        />,
      );
    }

    const txt = (text || '').replace('full-width', '');
    const image = <img src={href} alt={title} />;

    return `
      <figure>
        ${image}
        ${renderToString(
          <figcaption style={lightCaptionStyle}>{txt}</figcaption>,
        )}
      </figure>
    `;
  }

  paragraph(text: string) {
    if (text && !text.startsWith('[')) {
      this.pNum++;
    }

    let renderedText = text;

    // check if dropcap needed/requested
    if (
      text &&
      text.length > 1 &&
      (text.startsWith('^') ||
        (this.pNum === 1 && this.initialDropcap && /^[a-zA-Z\"]/.test(text)))
    ) {
      renderedText = renderToString(
        <DropCap
          dangerouslySetInnerHTML={{ __html: text.replace(/^\^/, '') }}
        />,
      );
    }

    return (super.paragraph as any)(renderYoutubeVideoParagraph(renderedText));
  }
}

/**
 * Renders a YouTube video iframe
 *
 * @example: [youtube-video nBu0rl5B70I]
 */
export const renderYoutubeVideoParagraph = (text: string) => {
  const isVideo = text.indexOf('[youtube-video') === 0;
  if (!isVideo) {
    return text;
  }

  const url = getVideoUrl(text);

  function videoId(videoUrl: string): string | null {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = videoUrl.match(regExp);

    return match && match[2].length === 11 ? match[2] : null;
  }

  const embedUrl: string = `https://www.youtube.com/embed/${videoId(
    url,
  )}?${qs.stringify({
    autoplay: 0,
    iv_load_policy: 3,
    playsinline: 1,
    rel: 0,
    showinfo: 0,
  })}`;

  const params = {} as any;
  const fullWidth = text.indexOf('[youtube-video-full') === 0;
  if (fullWidth) params.style = { maxWidth: 'unset' };

  const frame = (
    <iframe
      title="video"
      src={embedUrl}
      frameBorder="0"
      allowFullScreen
      allow="encrypted-media"
      {...params}
    />
  );

  if (url) {
    return renderToString(
      <ImageAspectBox16x9 data-testid="video-component" style={params.style}>
        {frame}
      </ImageAspectBox16x9>,
    );
  }

  return '';
};

function getVideoUrl(text: string) {
  try {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const [, , videoId] =
      /\[(youtube-video|youtube-video-full)\s+([\w-_]+)?\]/gi.exec(
        text,
      ) as any[];

    return `https://www.youtube.com/embed/${videoId}?iv_load_policy=3&playsinline=1&rel=0`;
  } catch (e) {
    // don't error out if we can't parse the YouTube ID
    console.error(e); // eslint-disable-line no-console
    return '';
  }
}
